/**
 * Feature Flag Service
 *
 * Manages feature flag state with intelligent caching to minimize backend calls.
 *
 * Usage:
 * ```
 * import { featureFlagService } from 'datastage/services/featureFlagService';
 *
 * // Get flags (cached for 30 seconds)
 * const isHyperlaneEnabled = await featureFlagService.isHyperlaneEnabled();
 *
 * // Clear cache (useful after config changes)
 * featureFlagService.clearCache();
 * ```
 */

import { apiClientV1 } from 'datastage/api';
import { getAuthToken } from 'datastage/utils/localStorageService';

class FeatureFlagService {
    constructor() {
        this._hyperlaneCache = null;
        this._cacheExpiry = null;
        this.CACHE_DURATION = 30 * 1000; // 30 seconds (reduced for manual flag changes)
    }

    /**
     * Check if Hyperlane feature flag is enabled.
     * Uses cached value if available and not expired.
     *
     * @returns {Promise<boolean>} True if Hyperlane is enabled, false otherwise
     */
    async isHyperlaneEnabled() {
        const now = Date.now();

        // Return cached value if still valid
        if (this._hyperlaneCache !== null && this._cacheExpiry && now < this._cacheExpiry) {
            return this._hyperlaneCache;
        }

        try {
            const { data } = await apiClientV1(getAuthToken()).get('/products/hyperlaneEnabled');
            this._hyperlaneCache = data === true;
            this._cacheExpiry = now + this.CACHE_DURATION;

            console.log('[FeatureFlags] Fetched from backend - Hyperlane:', this._hyperlaneCache);
            return this._hyperlaneCache;
        } catch (error) {
            console.error('[FeatureFlags] Error fetching Hyperlane flag, defaulting to false:', error);
            // Default to legacy (false) on error
            this._hyperlaneCache = false;
            this._cacheExpiry = now + this.CACHE_DURATION;
            return false;
        }
    }

    /**
     * Clear the cache, forcing next call to fetch from backend.
     * Useful when feature flags are updated in the backend.
     */
    clearCache() {
        console.log('[FeatureFlags] Cache cleared');
        this._hyperlaneCache = null;
        this._cacheExpiry = null;
    }

    /**
     * Get current cached value without fetching.
     * Returns null if not cached.
     *
     * @returns {boolean|null}
     */
    getCachedValue() {
        if (this._hyperlaneCache !== null && this._cacheExpiry && Date.now() < this._cacheExpiry) {
            return this._hyperlaneCache;
        }
        return null;
    }
}

// Export singleton instance
export const featureFlagService = new FeatureFlagService();
